import { ADDITIONAL_DATA_KEY, MAIN_DATA_KEY } from 'modules/Monitoring/consts/chart'
import { ChartData, ChartItemWitSelection } from 'modules/Monitoring/types'

export type HoveredLine = {
  name: string
  isAddition: boolean
}

export type HoveredPoint = HoveredLine & {
  chartValue: string
  payload: ChartData[0]
}

type FindHoveredPointParams = {
  payload: ChartData[0]
  pointer: { x: number; y: number }
  pointX: number
  charts: ChartItemWitSelection[]
  additionCharts: ChartItemWitSelection[]
  maxDistance: number
  getPixelY: (value: number) => number | undefined
}

type Candidate = HoveredPoint & { distance: number }

export const findHoveredPoint = ({
  payload,
  pointer,
  pointX,
  charts,
  additionCharts,
  getPixelY,
  maxDistance,
}: FindHoveredPointParams): HoveredPoint | undefined => {
  const toCandidate = (
    { name, value: chartValue, selected }: ChartItemWitSelection,
    isAddition: boolean,
  ): Candidate | undefined => {
    const dataValue =
      payload[isAddition ? ADDITIONAL_DATA_KEY : MAIN_DATA_KEY]?.[chartValue]
    const pixelY = selected && dataValue ? getPixelY(dataValue.value) : undefined

    if (pixelY === undefined) {
      return undefined
    }

    return {
      name,
      chartValue,
      payload,
      distance: Math.hypot(pointX - pointer.x, pixelY - pointer.y),
      isAddition,
    }
  }

  const nearest = [
    ...charts.map(chart => toCandidate(chart, false)),
    ...additionCharts.map(chart => toCandidate(chart, true)),
  ].reduce<Candidate | undefined>(
    (best, candidate) =>
      candidate && candidate.distance <= (best?.distance ?? maxDistance)
        ? candidate
        : best,
    undefined,
  )

  if (!nearest) {
    return undefined
  }

  const { distance, ...hoveredPoint } = nearest

  return hoveredPoint
}

export const isSameHoveredPoint = (a?: HoveredPoint, b?: HoveredPoint) =>
  a?.chartValue === b?.chartValue && a?.payload === b?.payload
